import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  Gavel,
  Upload,
  CheckCircle,
  Globe,
  Play,
} from "lucide-react";

export default function WhyItMatters() {
  const steps = [
  {
    number: 1,
    icon: Gavel,
    title: "Project Data Entry",
    description: "Officers and municipal authorities enter details of roads, gutters, and footpaths, including geo-coordinates, cost, material, and associated documents, ensuring complete and accurate data submission.",
    color: "bg-blue-600"
  },
  {
    number: 2,
    icon: Upload,
    title: "Infrastructure Recorded on Blockchain",
    description: "All approved project records are cryptographically secured and permanently recorded on the blockchain ledger, providing an immutable history of every infrastructure work.",
    color: "bg-accent"
  },
  {
    number: 3,
    icon: CheckCircle,
    title: "Transparent Validation",
    description: "Two-level validation ensures that each project is verified for accuracy and authenticity, eliminating errors, duplication, and misuse of public funds.",
    color: "bg-green-600"
  },
  {
    number: 4,
    icon: Globe,
    title: "Publicly Accessible Records",
    description: "All finalized infrastructure records are visible to citizens through a public portal, enabling transparency, trust, and accountability in municipal project management.",
    color: "bg-purple-600"
  }
];

  return (
    <section id="why-it-matters" className="py-20 bg-secondary/30">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Why It Matters
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
Understanding the complete lifecycle of infrastructure projects on blockchain — from project entry to permanent public record.
          </p>
        </div>

        {/* Timeline Steps */}
        <div className="relative">
          {/* Desktop Timeline */}
          <div className="hidden lg:block">
            <div className="relative">
              {/* Timeline Line */}
              <div className="absolute top-24 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 via-accent via-green-600 to-purple-600 rounded-full"></div>

              <div className="grid grid-cols-4 gap-8">
                {steps.map((step, index) => {
                  const IconComponent = step.icon;
                  return (
                    <div key={index} className="text-center relative">
                      {/* Step Circle */}
                      <div className={`w-16 h-16 ${step.color} rounded-full mx-auto mb-6 flex items-center justify-center relative z-10 shadow-lg`}>
                        <IconComponent className="w-8 h-8 text-white" />
                      </div>

                      <Card className="hover:shadow-lg transition-shadow duration-300">
                        <CardContent className="p-6">
                          <div className={`w-8 h-8 ${step.color} rounded-full mx-auto mb-4 flex items-center justify-center`}>
                            <span className="text-white font-bold text-sm">{step.number}</span>
                          </div>
                          <h3 className="font-semibold text-foreground mb-3 text-balance">
                            {step.title}
                          </h3>
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {step.description}
                          </p>
                        </CardContent>
                      </Card>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Mobile Timeline */}
          <div className="lg:hidden space-y-8">
            {steps.map((step, index) => {
              const IconComponent = step.icon;
              return (
                <div key={index} className="relative">
                  {/* Connector Line */}
                  {index < steps.length - 1 && (
                    <div className="absolute left-8 top-16 w-0.5 h-16 bg-gradient-to-b from-primary to-accent"></div>
                  )}

                  <div className="flex items-start space-x-4">
                    <div className={`w-16 h-16 ${step.color} rounded-full flex items-center justify-center shadow-lg relative z-10`}>
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>

                    <Card className="flex-1">
                      <CardContent className="p-6">
                        <div className="flex items-center space-x-3 mb-3">
                          <div className={`w-8 h-8 ${step.color} rounded-full flex items-center justify-center`}>
                            <span className="text-white font-bold text-sm">{step.number}</span>
                          </div>
                          <h3 className="font-semibold text-foreground">
                            {step.title}
                          </h3>
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {step.description}
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Video/Infographic Section */}
        <div className="mt-20">
          <Card className="overflow-hidden bg-gradient-to-br from-primary/5 to-accent/5 border-primary/20">
            <CardContent className="p-8">
              <div className="grid lg:grid-cols-2 gap-8 items-center">
                <div className="space-y-6">
                  <h3 className="text-2xl font-semibold text-foreground">
                    See How It Works
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Watch how our blockchain-powered system ensures complete
                    transparency in amenity auctions — from announcement to final results.
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="text-sm text-foreground">Step-by-step auction lifecycle</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="text-sm text-foreground">Blockchain-backed trust and fairness</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="text-sm text-foreground">Empowering citizens & stakeholders</span>
                    </div>
                  </div>
                  <Button className="bg-primary hover:bg-primary/90 text-primary-foreground">
                    Watch Demo Video
                    <Play className="ml-2 w-4 h-4" />
                  </Button>
                </div>

                {/* Video Placeholder */}
                <div className="relative">
                  <div className="aspect-video bg-gradient-to-br from-primary/20 to-accent/20 rounded-lg flex items-center justify-center relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-accent/10"></div>
                    <Button
                      size="lg"
                      className="bg-white/90 hover:bg-white text-primary shadow-lg relative z-10"
                    >
                      <Play className="w-6 h-6 mr-2" />
                      Play Video
                    </Button>

                    {/* Decorative elements */}
                    <div className="absolute top-4 left-4 w-3 h-3 bg-accent rounded-full opacity-60 animate-pulse"></div>
                    <div className="absolute bottom-6 right-6 w-4 h-4 bg-primary rounded-full opacity-40 animate-pulse delay-1000"></div>
                    <div className="absolute top-1/2 left-6 w-2 h-2 bg-white rounded-full opacity-50 animate-pulse delay-500"></div>
                  </div>

                  <div className="mt-4 text-center">
                    <p className="text-sm text-muted-foreground">
                      "Blockchain for Infrastructure" - 5:30 mins
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      <style>{`
  /* Animate glowing circles */
  .step-circle {
    position: relative;
    box-shadow: 0 0 15px rgba(0,0,0,0.1);
    transition: transform 0.3s ease, box-shadow 0.3s ease;
  }
  .step-circle:hover {
    transform: scale(1.1);
    box-shadow: 0 0 25px rgba(59,130,246,0.7); /* bluish glow */
  }

  /* Animated pulse glow */
  .pulse-glow {
    animation: pulseGlow 2s infinite;
  }
  @keyframes pulseGlow {
    0% { box-shadow: 0 0 0px rgba(59,130,246,0.4); }
    50% { box-shadow: 0 0 20px rgba(59,130,246,0.8); }
    100% { box-shadow: 0 0 0px rgba(59,130,246,0.4); }
  }

  /* Animated connecting arrows */
  .timeline-line {
    background: linear-gradient(90deg, #2563eb, #06b6d4, #16a34a, #9333ea);
    background-size: 300% 100%;
    animation: moveLine 4s linear infinite;
  }
  @keyframes moveLine {
    0% { background-position: 0% 50%; }
    100% { background-position: 100% 50%; }
  }

  /* Card hover lift */
  .auction-card {
    transition: transform 0.3s ease, box-shadow 0.3s ease;
  }
  .auction-card:hover {
    transform: translateY(-6px);
    box-shadow: 0 10px 20px rgba(0,0,0,0.15);
  }

  /* Video play button pulse */
  .play-btn {
    animation: btnPulse 2s infinite;
  }
  @keyframes btnPulse {
    0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(37,99,235,0.4); }
    70% { transform: scale(1.1); box-shadow: 0 0 20px 10px rgba(37,99,235,0); }
    100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(37,99,235,0); }
  }
`}</style>

    </section>
  );
}
